import { motion } from 'motion/react';
import { useEffect, useState } from 'react';

import DoctorCard from '../components/DoctorCard';
import { useLanguage, useTranslation } from '../i18n/hooks';

function OurDoctors() {
  const [currentPage, setCurrentPage] = useState(1);
  const doctorsPerPage = 9; // 3 rows x 3 columns
  const { direction, language } = useLanguage();
  const t = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Calculate pagination
  const totalPages = Math.ceil(doctorsData.length / doctorsPerPage);
  const startIndex = (currentPage - 1) * doctorsPerPage;
  const endIndex = startIndex + doctorsPerPage;
  const currentDoctors = doctorsData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main
      dir={direction}
      lang={language}
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16"
    >
      {/* Header Section */}
      <section className="px-4 pt-32 sm:px-6 md:px-8 lg:px-32">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-between gap-4 self-stretch"
          >
            {/* Main Heading */}
            <h1 className="text-right text-[32px] leading-[1.2] font-bold text-black">
              {t.doctors.title}
            </h1>

            {/* Back to Home Link */}
            <a
              href="/"
              className="flex items-center gap-2 text-[#2AB25F] transition-opacity hover:opacity-80"
            >
              <span className="text-right text-xl leading-[1.2] font-bold">
                {t.doctors.backToHome}
              </span>
              <img
                src="/images/arrow-left.svg"
                alt={t.common.arrowAlt}
                className="h-6 w-6"
              />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Doctors Grid */}
      <section className="px-4 py-8 sm:px-6 md:px-8 lg:px-32">
        <div className="mx-auto max-w-7xl">
          {/* Doctors Grid */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {currentDoctors.map((doctor, index) => (
              <DoctorCard key={index} doctor={doctor} index={index} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-12 flex items-center justify-center gap-4"
            >
              {/* Previous Button */}
              {/* <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex h-6 w-6 items-center justify-center rounded-lg transition-all disabled:cursor-not-allowed disabled:opacity-30"
                aria-label={t.doctors.pagination.previous}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button> */}

              {/* Page Numbers */}
              <div className="flex items-center gap-3">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`flex h-11 min-w-[44px] items-center justify-center rounded-[200px] px-2.5 text-xl font-bold transition-all ${
                        currentPage === page
                          ? 'bg-[#F2F2F2] text-[#1D1B20]'
                          : 'text-[#1D1B20] hover:bg-gray-100'
                      }`}
                      aria-label={t.doctors.pagination.page.replace(
                        '{page}',
                        page.toString(),
                      )}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>

              {/* Next Button */}
              {/* <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex h-6 w-6 items-center justify-center rounded-lg transition-all disabled:cursor-not-allowed disabled:opacity-30"
                aria-label={t.doctors.pagination.next}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button> */}
            </motion.div>
          )}
        </div>
      </section>

      {/* What Distinguishes Our Doctors Section */}
      <section className="px-4 py-16 sm:px-6 md:px-8 lg:px-32">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-10"
          >
            {/* Heading */}
            <h2 className="text-[32px] leading-[1.2] font-bold text-black">
              {t.doctors.distinguishingTitle}
            </h2>

            {/* Feature Cards */}
            <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-3">
              {t.doctors.distinguishingFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
                  className={`flex flex-col items-end rounded-xl bg-[rgba(223,255,233,0.6)] p-6 ${
                    index === 0 ? 'relative gap-6' : 'gap-2.5'
                  }`}
                >
                  {index === 0 && (
                    <div className="absolute top-0 right-0 left-0 h-1 rounded-t-xl bg-[#2AB25F]" />
                  )}
                  <p className="text-right text-lg leading-[1.2] font-bold text-black">
                    {feature}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

export default OurDoctors;

const doctorsData: Array<{
  name: string;
  title: string;
  image: string | null;
  bio: string;
  gender?: 'male' | 'female';
}> = [
  {
    name: 'Dr. Fahad Al Mugbel - د. فهد المقبل',
    title: 'Medical Professional',
    image:
      'https://tgl.emalaf.com/ContentFiles/UserImages/d4369594-c2a3-4230-8800-20756c4cb57b-Dr. fahad phot.jpg',
    bio: 'Consultant, Genitourinary & Gastrointestinal Oncology - طبيب استشاري - أورام الجهازين الهضمي و التناسلي',
  },
  {
    name: 'Dr. Riad Al Fakeih - د. رياض الفقيه',
    title: 'Medical Professional',
    image:
      'https://tgl.emalaf.com/ContentFiles/UserImages/1da420c2-6424-43fe-af95-1946573111e4-Dr. riad photo.jpg',
    bio: 'Consultant, Lymphoma / Myeloma - طبيب استشاري - ألأورام لمفاوية والورم العقدي التعددي',
  },
  {
    name: 'Dr. Ahmed Al Otaibi - د. أحمد العتيبي',
    title: 'Medical Professional',
    image:
      'https://tgl.emalaf.com/ContentFiles/UserImages/a86cd3b6-5c88-4d05-8148-1b10448e1792-Dr. ahmed photo.jpg',
    bio: 'Consultant, Acute Leukemia - طبيب استشاري - سرطان الدم الحاد',
  },
  {
    name: 'Dr. Sabah Al Aklabi - د. صباح الأكلبي',
    title: 'Medical Professional',
    image: null,
    bio: 'Consultant, Breast Cancer - طبيب استشاري لسرطان الثدي',
  },
  {
    name: 'Dr. Al Fadel Al Shaibani - د. الفضل الشيباني',
    title: 'Medical Professional',
    image:
      'https://tgl.emalaf.com/ContentFiles/UserImages/5e2b366f-d68e-4668-a313-c9600c61c7be-Dr. Alfadel photo.jpg',
    bio: 'Consultant, Lymphoma / Myeloma - طبيب استشاري - الأورام الليمفاوية و الورم العقدي التعددي',
  },
  {
    name: 'Dr. Sami Al-Johani - د. سامي الجهني',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Adult Invasive Cardiology - KFSHRC Madinah - طبيب استشاري أمراض القلب التداخلية - فرع المدينة',
  },
  {
    name: 'Dr. Ihab AlShelli - د.إيهاب الشيلي',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Pulmonary Medicine - KFSHRC Madinah - طبيب إستشاري أمراض صدرية - فرع المدينة',
  },
  {
    name: 'Dr. Badr Al-Joaid - د. بدر الجعيد',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Abdominal Radiology - KFSHRC Madinah - طبيب إستشاري - الأشعة التداخلية',
  },
  {
    name: 'Dr. Tamadhir Gazzaz - د. تماضر قزاز',
    title: 'Medical Professional',
    image: null,
    gender: 'female',
    bio: 'Consultant, Cardiothoracic Radiology - KFSHRC Madinah - طبيب إستشاري - أشعة القلب والقفص الصدري',
  },
  {
    name: 'Dr. Basim Felemban - د. باسم فلمبان',
    title: 'Medical Professional',
    image:
      'https://tgl.emalaf.com/ContentFiles/UserImages/e8f736ac-e570-45e4-95da-fe8adc050d1f-Dr. Basim photo.jpg',
    bio: 'Consultant, Interventional Radiology - KFSHRC Madinah - طبيب استشاري أشعة تداخلية',
  },
  {
    name: 'Dr. Ayman El homoudi - د. أيمن الحمودي',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, General Pediatrics - KFSHRC Madinah - طبيب استشاري أطفال',
  },
  {
    name: 'Dr. Yousef Sebeih - د. يوسف صبيح',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Pediatric Nephrology - KFSHRC Madinah - طبيب استشاري كلى أطفال',
  },
  {
    name: 'Dr. Sharifah Al-Ghamdi - د. شريفه الغامدي',
    title: 'Medical Professional',
    image: null,
    gender: 'female',
    bio: 'Consultant, Pediatric Gastroenterology - KFSHRC Madinah - طبيب استشاري جهاز هضمي أطفال',
  },
  {
    name: 'Dr. Mohannad Rajab - د. مهند رجب',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Associate Consultant, Otolaryngology - KFSHRC Madinah - طبيب استشاري أنف وأذن و حنجرة',
  },
  {
    name: 'Dr. Fayyaz Mazari - د. فياض مزاري',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Breast And Endocrine Surgery - KFSHRC Madinah - طبيب استشاري جراحة الثدي والغدد الصماء',
  },
  {
    name: 'Dr. Mouhab AlJajeh - د. مهاب الجاجيه',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Ophthalmology - KFSHRC Madinah - طبيب استشاري عيون',
  },
  {
    name: 'Dr. Safaa Medani - د. صفاء مداني',
    title: 'Medical Professional',
    image: null,
    gender: 'female',
    bio: 'Assistant Consultant Ophthalmology - KFSHRC Madinah - طبيب مساعد استشاري عيون',
  },
  {
    name: 'Dr. Rasha Farahat - د. رشا رفعت',
    title: 'Medical Professional',
    image: null,
    gender: 'female',
    bio: 'Assistant Consultant, Gynecology Oncology - KFSHRC Madinah - طبيب مساعد استشاري اورام نساء',
  },
  {
    name: 'Dr. Mahmood Samman - د. محمود سمّان',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Oral And Maxillofacial Surgery - KFSHRC Madinah - طبيب استشاري جراحة وجه و فكين',
  },
  {
    name: 'Dr. Hani Ghabbani - د. هاني غباني',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Endodontics - KFSHRC Madinah - طبيب استشاري علاج جذور الأسنان',
  },
  {
    name: 'Dr. Husam El Baraasi - د. حسام البعرصي',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Anesthesiology - KFSHRC Madinah - طبيب استشاري تخدير و طب الألم',
  },
  {
    name: 'Dr. Kamal Ashtewi - د. كمال اشتيوي',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Anesthesiology - KFSHRC Madinah - طبيب استشاري تخدير و طب الألم',
  },
  {
    name: 'Dr. Mohammad Shagrani - د. محمد شقراني',
    title: 'Medical Professional',
    image: null,
    gender: 'male',
    bio: 'Consultant, Pediatric transplant gastro & hepatology - KFSHRC Riyadh - استشاري زراعة الكبد أطفال - مستشفى الملك فيصل التخصصي - الرياض',
  },
];
